/**
 * Domain models for Product Listing Page (PLP)
 * -------------------------------------------
 * These classes represent the core business entities for PLP.
 */

export type { ListingFilterItem, ListingFilter, PlpFiltersResult } from '@/features/filters/domain/models/Filter';
import type { ListingFilter } from '@/features/filters/domain/models/Filter';

export type { ListingSeo, ExploreLink, ListingFaqItem } from '@/shared/features/seo/domain/models/Seo';
import type { ListingSeo, ExploreLink, ListingFaqItem } from '@/shared/features/seo/domain/models/Seo';

export interface ListingDesignItem {
  designId: number;
  designName: string;
  imageUrl: string;
  productPageUrl: string;
  price: string;
  discountedPrice?: string;
  discountPercent?: number;
  discountOnDiamondPriceValue?: number;
  discountName?: string;
  discountValue?: string;
  customizationId?: number;
  videoUrl?: string;
  videoPosterUrl?: string;
  hoverImageUrl?: string;
  /** Category type from API (e.g. "rings", "earrings") — used for analytics */
  categoryType?: string;
  /** Metal / color from API (e.g. "Yellow Gold") — used for analytics */
  metal?: string;
  /** 1024px image URL from mediaItems carousel — used for analytics */
  analyticsImageUrl?: string;
  sourceType?: string;
}

export interface ListingMeta {
  /** Category/category title (e.g. "Rings") – shown in header banner */
  header: string;
  /** Optional banner background image URL for header */
  headerImageUrl?: string;
  totalCount: number;
  filters: ListingFilter[];
  /** URL to navigate when all filters are reset (from browse API) */
  filtersResetUrl?: string;
  /** Static SEO links shown under "Explore other designs" – come from API (SeoLinks) */
  exploreLinks?: ExploreLink[];
  /** SEO from browse API – Title, Description, Canonical, etc. */
  listingSeo?: ListingSeo;
  /** Category FAQs for footer content (non-store browse PLPs). */
  faqs?: ListingFaqItem[];
}

export interface ListingPageResult {
  header: string;
  headerImageUrl?: string;
  totalCount: number;
  filters: ListingFilter[];
  /** URL to navigate when all filters are reset (from browse API) */
  filtersResetUrl?: string;
  designs: ListingDesignItem[];
  /** Static SEO links under "Explore other designs" (SeoLinks from API) */
  exploreLinks?: ExploreLink[];
  /** When is_filtered was sent and backend wants to canonicalise the URL */
  redirectUrl?: string;
  /** SEO from browse API for head + seoText block */
  listingSeo?: ListingSeo;
  /** Category FAQs for footer SEO block */
  faqs?: ListingFaqItem[];
}

/** Fetch function signature for usePlpListing – one page at a time */
export type FetchListingPage = (
  pageNumber: number,
  opts?: { signal?: AbortSignal }
) => Promise<ListingPageResult>;

export class PlpDesignItem {
  designId: number;
  designName: string;
  imageUrl: string;
  productPageUrl: string;
  price: string;
  discountedPrice?: string;
  discountPercent?: number;
  discountOnDiamondPriceValue?: number;
  discountName?: string;
  discountValue?: string;
  customizationId?: number;
  videoUrl?: string;
  videoPosterUrl?: string;
  hoverImageUrl?: string;
  categoryType?: string;
  metal?: string;
  analyticsImageUrl?: string;
  sourceType?: string;

  constructor(props: {
    designId: number;
    designName: string;
    imageUrl: string;
    productPageUrl: string;
    price: string;
    discountedPrice?: string;
    discountPercent?: number;
    discountOnDiamondPriceValue?: number;
    discountName?: string;
    discountValue?: string;
    customizationId?: number;
    videoUrl?: string;
    videoPosterUrl?: string;
    hoverImageUrl?: string;
    categoryType?: string;
    metal?: string;
    analyticsImageUrl?: string;
    sourceType?: string;
  }) {
    this.designId = props.designId;
    this.designName = props.designName;
    this.imageUrl = props.imageUrl;
    this.productPageUrl = props.productPageUrl;
    this.price = props.price;
    this.discountedPrice = props.discountedPrice;
    this.discountPercent = props.discountPercent;
    this.discountOnDiamondPriceValue = props.discountOnDiamondPriceValue;
    this.discountName = props.discountName;
    this.discountValue = props.discountValue;
    this.customizationId = props.customizationId;
    this.videoUrl = props.videoUrl;
    this.videoPosterUrl = props.videoPosterUrl;
    this.hoverImageUrl = props.hoverImageUrl;
    this.categoryType = props.categoryType;
    this.metal = props.metal;
    this.analyticsImageUrl = props.analyticsImageUrl;
    this.sourceType = props.sourceType;
  }
}

export class PlpFilterItem {
  name: string;
  displayName: string;
  count: number;
  url: string;
  selected: boolean;

  constructor(props: {
    name: string;
    displayName: string;
    count: number;
    url: string;
    selected: boolean;
  }) {
    this.name = props.name;
    this.displayName = props.displayName;
    this.count = props.count;
    this.url = props.url;
    this.selected = props.selected;
  }
}

export class PlpFilter {
  category: string;
  items: PlpFilterItem[];

  constructor(props: { category: string; items: PlpFilterItem[] }) {
    this.category = props.category;
    this.items = props.items;
  }
}

/** SEO payload from API (shape matches presentation ListingSeo) */
export type PlpSeoData = Record<string, unknown>;

export class ProductListing {
  header: string;
  totalCount: number;
  filters: PlpFilter[];
  designs: PlpDesignItem[];
  /** When is_filtered was sent and backend returned redirect_url */
  redirectUrl?: string;
  /** URL to navigate when all filters are reset (from browse API) */
  filtersResetUrl?: string;
  /** SEO from browse API (Title, Description, Canonical, seoText, etc.) */
  listingSeo?: PlpSeoData;
  faqs?: ListingFaqItem[];

  constructor(props: {
    header: string;
    totalCount: number;
    filters: PlpFilter[];
    designs: PlpDesignItem[];
    redirectUrl?: string;
    filtersResetUrl?: string;
    listingSeo?: PlpSeoData;
    faqs?: ListingFaqItem[];
  }) {
    this.header = props.header;
    this.totalCount = props.totalCount;
    this.filters = props.filters;
    this.designs = props.designs;
    this.redirectUrl = props.redirectUrl;
    this.filtersResetUrl = props.filtersResetUrl;
    this.listingSeo = props.listingSeo;
    this.faqs = props.faqs;
  }
}

